import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import type { ButtonProps } from 'antd';
import { Button, Col, Row, Space } from 'antd';
import { TbMailCheck, TbMailFast, TbMailShare, TbProgressCheck, TbUserCheck, TbUserQuestion } from 'react-icons/tb';

import { useCooldown } from '@/hooks/useCooldown';
import { userService } from '@/services/database';
import { isApiServiceError } from '@/services/http/errors';
import { PlatformAlert } from '@/shared/components/alerts';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useUserStore } from '@/stores/database/entities/00-RBAC/01-Users/store';

const VERIFICATION_RESEND_COOLDOWN_SECONDS = 30;

type AccountVerificationPhase = 'error' | 'idle' | 'sent';

interface AccountVerificationControlProps {
    checkOnMount?: boolean;
    initialEmailSent?: boolean;
    layout?: 'compact' | 'responsive';
    sendButtonType?: ButtonProps['type'];
    showStatus?: boolean;
}

const getRateLimitRetryAfterSeconds = (error: unknown) => {
    if (!isApiServiceError(error) || error.code !== 'RATE_LIMITED') {
        return VERIFICATION_RESEND_COOLDOWN_SECONDS;
    }

    const data = error.data as { error?: { details?: { retryAfterSeconds?: unknown } } } | undefined;
    const retryAfterSeconds = Number(data?.error?.details?.retryAfterSeconds);

    return Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
        ? Math.ceil(retryAfterSeconds)
        : VERIFICATION_RESEND_COOLDOWN_SECONDS;
};

/** Shared email-verification lifecycle used by onboarding and account settings. */
const AccountVerificationControl: FC<AccountVerificationControlProps> = ({
    checkOnMount = false,
    initialEmailSent = false,
    layout = 'compact',
    sendButtonType = 'dashed',
    showStatus = false,
}) => {
    const { selectedUser: user, updateSelectedUserLocal } = useUserStore();
    const [confirmed, setConfirmed] = useState(Boolean(user?.emailConfirmed));
    const [operation, setOperation] = useState<'checking' | 'sending'>();
    const [phase, setPhase] = useState<AccountVerificationPhase>(initialEmailSent ? 'sent' : 'idle');
    const checkedOnMountRef = useRef(false);
    const { remainingSeconds: resendCooldown, start: startResendCooldown } = useCooldown({
        seconds: VERIFICATION_RESEND_COOLDOWN_SECONDS,
    });

    useEffect(() => {
        setConfirmed(Boolean(user?.emailConfirmed));
        if (!user?.emailConfirmed) {
            setPhase(initialEmailSent ? 'sent' : 'idle');
        }
    }, [initialEmailSent, user?.emailAddress, user?.emailConfirmed]);

    const checkVerification = useCallback(
        async (announce = true) => {
            if (!user?.id || operation) {
                return;
            }

            try {
                setOperation('checking');
                const authenticatedUser = await userService.getAuthenticated();
                const refreshedUser = authenticatedUser.data;
                const emailConfirmed = Boolean(refreshedUser?.emailConfirmed);

                if (refreshedUser) {
                    updateSelectedUserLocal(refreshedUser);
                }

                setConfirmed(emailConfirmed);
                setPhase(emailConfirmed ? 'idle' : 'sent');

                if (announce) {
                    if (emailConfirmed) {
                        toast('Account Verified', {
                            description: refreshedUser?.emailAddress ?? user.emailAddress,
                            icon: <TbUserCheck size='1rem' />,
                        });
                    } else {
                        toast.warning('Verification Pending', {
                            description: `Check ${user.emailAddress} and try again.`,
                        });
                    }
                }
            } catch (error) {
                setPhase('error');
                captureException(error, { tags: { action: 'check-account-verification' } });
                if (announce) {
                    toast.error('Unable to Check Verification', {
                        description: 'Please try again.',
                    });
                }
            } finally {
                setOperation(undefined);
            }
        },
        [operation, updateSelectedUserLocal, user],
    );

    useEffect(() => {
        if (!checkOnMount || checkedOnMountRef.current) {
            return;
        }

        checkedOnMountRef.current = true;
        void checkVerification(false);
    }, [checkOnMount, checkVerification]);

    const sendVerificationEmail = async () => {
        if (!user?.id || operation || resendCooldown > 0) {
            return;
        }

        try {
            setOperation('sending');
            await userService.resendVerificationEmail(user.id);
            setPhase('sent');
            startResendCooldown();
            toast('Verification Email Sent', {
                description: user.emailAddress,
                icon: <TbMailFast size='1rem' />,
            });
        } catch (error) {
            if (isApiServiceError(error) && error.code === 'RATE_LIMITED') {
                const retryAfterSeconds = getRateLimitRetryAfterSeconds(error);
                setPhase('sent');
                startResendCooldown(retryAfterSeconds);
                toast.warning('Please Wait Before Resending', {
                    description: `Try again in ${retryAfterSeconds} seconds.`,
                });
            } else {
                setPhase('error');
                captureException(error, { tags: { action: 'send-account-verification' } });
                toast.error('Unable to Send Verification Email', {
                    description: 'Please try again.',
                });
            }
        } finally {
            setOperation(undefined);
        }
    };

    const busy = operation !== undefined;
    const emailSent = initialEmailSent || phase === 'sent' || resendCooldown > 0;
    const showCheckAction = emailSent || operation === 'checking';
    const sendLabel = resendCooldown > 0 ? `Resend in ${resendCooldown}s` : emailSent ? 'Resend Email' : 'Verify';
    const sendButton = (
        <Button
            block
            disabled={!user?.id || busy || resendCooldown > 0}
            icon={<TbMailShare size='1rem' style={{ verticalAlign: '-0.175rem' }} />}
            loading={operation === 'sending'}
            type={sendButtonType}
            onClick={() => void sendVerificationEmail()}
        >
            {sendLabel}
        </Button>
    );
    const checkButton = showCheckAction ? (
        <Button
            block
            disabled={!user?.id || busy}
            icon={<TbProgressCheck size='1rem' style={{ verticalAlign: '-0.175rem' }} />}
            loading={operation === 'checking'}
            onClick={() => void checkVerification()}
        >
            Check Verification
        </Button>
    ) : null;
    const actions = confirmed ? (
        <Button block disabled icon={<TbMailCheck size='1rem' style={{ verticalAlign: '-0.175rem' }} />} type='dashed'>
            Verified
        </Button>
    ) : layout === 'responsive' && checkButton ? (
        <Row gutter={[12, 12]}>
            <Col xs={24} sm={24} md={12}>
                {sendButton}
            </Col>
            <Col xs={24} sm={24} md={12}>
                {checkButton}
            </Col>
        </Row>
    ) : checkButton ? (
        <Space.Compact block>
            {sendButton}
            {checkButton}
        </Space.Compact>
    ) : (
        sendButton
    );

    if (!showStatus) {
        return actions;
    }

    const status = confirmed
        ? {
              description: (
                  <span>
                      <strong>{user?.emailAddress}</strong> has been verified!
                  </span>
              ),
              icon: <TbUserCheck />,
              message: 'Account Verified!',
              type: 'success' as const,
          }
        : phase === 'error'
          ? {
                description: 'We could not complete the verification request. Please try again.',
                icon: <TbUserQuestion />,
                message: 'Verification Unavailable',
                type: 'error' as const,
            }
          : {
                description: (
                    <span>
                        Please check your email at <strong>{user?.emailAddress}</strong> to verify your account.
                    </span>
                ),
                icon: <TbUserQuestion />,
                message:
                    operation === 'checking'
                        ? 'Checking Verification'
                        : emailSent
                          ? 'Pending Verification'
                          : 'Verify Your Account',
                type: 'warning' as const,
            };

    return (
        <Space direction='vertical' size='middle' style={{ width: '100%' }}>
            <PlatformAlert
                description={status.description}
                icon={status.icon}
                message={status.message}
                showIcon
                type={status.type}
            />
            {confirmed ? null : actions}
        </Space>
    );
};

export type { AccountVerificationControlProps };
export default AccountVerificationControl;
